import { SpineEventData } from "../../game/model/data/SpineEventData";
/**
 * SpineUtil.ts
 */
export class SpineUtil {
    /**
     * Removes all start/end event handlers and stops the animation
     * @param spine - Spine object instance to use
     */
    static reset(spine) {
        spine.removeCompleteHandlers();
        spine.removeStartHandlers();
        this.stop(spine);
    }
    /**
     * Stops and hides the animation
     * @param spine - Spine object instance to use
     */
    static stop(spine) {
        spine.stop();
        spine.visible = false;
    }
    /**
     * Advances the animation to a specific frame and pauses it
     * @param spine - Spine object instance to use
     * @param frame - Frame index
     * @param animation - Animation to play. Accepts either a single string or a two-string array providing landscape and portrait animations ([landscapeAnim, portraitAnim])
     * @param track - Spine track index to play the animation on (default: 0)
     * @param loop - Set true to loop the animation back to frame 0 after resume + reaching the end
     */
    static gotoAndStop(spine, frame, animation, track = 0, loop = false) {
        spine.removeCompleteHandler(track);
        spine.gotoAndStop(frame, animation, track, loop);
        spine.visible = true;
    }
    /**
     * Advances the animation to a specific frame and starts playing
     * @param spine - Spine object instance to use
     * @param frame - Frame index
     * @param animation - Animation to play. Accepts either a single string or a two-string array providing landscape and portrait animations ([landscapeAnim, portraitAnim])
     * @param track - Spine track index to play the animation on (default: 0)
     * @param loop - Set true to loop the animation back to frame 0 after reaching the end
     */
    static gotoAndPlay(spine, frame, animation, track = 0, loop = false) {
        spine.removeCompleteHandler(track);
        spine.gotoAndPlay(frame, animation, track, loop);
        spine.visible = true;
    }
    /**
     * Plays the spine animation in a loop
     * @param spine - Spine object instance to use
     * @param animation - Animation to play. Accepts either a single string or a two-string array providing landscape and portrait animations ([landscapeAnim, portraitAnim])
     * @param track - Spine track index to play the animation on (default: `0`)
     */
    static playLoop(spine, animation, track = 0) {
        this.playSpine(spine, animation, true, track);
    }
    static playOnce(spine, animation, track = 0, hideOnComplete = false, customHandler, scope) {
        spine.removeCompleteHandler(track);
        const handler = () => {
            if (hideOnComplete) {
                SpineUtil.stop(spine);
            }
            else {
                spine.pause();
                spine.playing = false;
            }
            customHandler?.call(scope, track);
        };
        spine.onComplete(handler, null, track);
        this.playSpine(spine, animation, false, track);
    }
    /**
     * Plays the selected spine animation once, with an option to add a complete listenter
     * @param spine - Spine object instance to use
     * @param animation - Animation to play. Accepts either a single string or a two-string array providing landscape and portrait animations ([landscapeAnim, portraitAnim])
     * @param handler - Callback function to play on completion of the animation
     * @param scope - Handler function scope
     * @param track - Spine track index to play the animation on (default: `0`)
     */
    static play(spine, animation, handler, scope, track = 0) {
        if (!animation) {
            animation = spine.getProperties()
                .defaultAnimation;
        }
        spine.removeCompleteHandler(track);
        if (handler) {
            spine.onComplete(handler, scope, track);
        }
        this.playSpine(spine, animation, false, track);
    }
    /**
     * Finds the named slot on the spine animation and replaces the texture with the provided one. Texture must be identical in size to the original texture or it will be stretched
     * @param spine - Spine object instance to use
     * @param animation - Animation that includes the slot that will be retextured - accepts either a single string or a two-string array providing landscape and portrait animations ([landscapeAnim, portraitAnim])
     * @param slotName - Name of the slot to be retextured
     * @param texture - Texture to replace the slot texture with
     */
    static setSlotTexture(spine, animation, slotName, texture) {
        spine.setSlotTexture(animation, slotName, texture);
    }
    /**
     * Searches the spineData for animation(s) with the given name(s) and returns the duration(s) in seconds
     * @param spine - Spine object instance to use
     * @param animation - Animation name(s). Accepts a single string or an array of strings, (for multiple anims, returns the durations in the same order)
     */
    static getDuration(spine, animation) {
        const spineData = spine.getSpine()?.spineData;
        if (spineData) {
            const animNames = Array.isArray(animation)
                ? animation.slice()
                : [animation];
            const animations = spineData.animations.filter((anim) => animNames.indexOf(anim.name) > -1);
            if (animations.length > 1) {
                return animations.map((anim) => anim.duration);
            }
            else if (animation.length === 1) {
                return animations[0].duration;
            }
        }
        return null;
    }
    static playSpine(spine, animation, loop = false, track = 0) {
        if (!animation) {
            animation = spine.getProperties()
                .defaultAnimation;
        }
        spine.play(animation, loop, true, track);
        spine.visible = true;
    }
    /**
     * Adds an eventListener for a custom spine event that has a name
     * @param spine - Spine object instance to use
     * @param name - Event name
     * @param handler - Callback function to play on completion of the animation
     * @param scope - Handler function scope
     * @param track - Spine track index to play the animation on (default: `0`)
     */
    static addNamedEventListener(spine, name, handler, scope, track) {
        const scopedHandler = handler.bind(scope);
        spine.onEvent((eventData) => {
            if (eventData[1]?.data?.name === name) {
                scopedHandler(new SpineEventData(eventData[1]?.data));
            }
        }, this, track);
    }
    /**
     * Adds a an eventListener for any/all custom spine events
     * @param spine - Spine object instance to use
     * @param handler - callback function to play on completion of the animation
     * @param scope - handler function scope
     * @param track - spine track index to play the animation on (default: 0)
     */
    static addEventListener(spine, handler, scope, track) {
        const scopedHandler = handler.bind(scope);
        spine.onEvent((eventData) => {
            if (eventData[1]?.data?.name !== undefined) {
                scopedHandler(new SpineEventData(eventData[1]?.data));
            }
        }, this, track);
    }
    /**
     * Set the mix duration for all possible animation transitions to the same value
     * @param spine - Spine object to use
     * @param duration - mix duration in s
     */
    static setAllMixes(spine, duration) {
        const animations = spine
            .getSpine()
            .spineData.animations.map((anim) => anim.name);
        animations.forEach((fromAnim) => {
            animations.forEach((toAnim) => {
                spine.setMix(duration, fromAnim, toAnim);
            });
        });
    }
}
//# sourceMappingURL=SpineUtil.js.map